import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import {
  grantAnalyticsConsent,
  denyAnalyticsConsent,
  getConsent,
} from "../lib/analytics";

const REOPEN_EVENT = "rci:reopen-cookie-banner";

/**
 * Programmatically re-open the cookie banner (used by Footer + Privacy Policy
 * "manage preferences" buttons).
 */
export function reopenCookieBanner() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(REOPEN_EVENT));
}

export const CookieBanner = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  // Show on first visit (no consent yet)
  useEffect(() => {
    const saved = getConsent();
    if (!saved) {
      const id = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(id);
    }
  }, []);

  // Allow re-opening via the global event
  useEffect(() => {
    const onReopen = () => setVisible(true);
    window.addEventListener(REOPEN_EVENT, onReopen);
    return () => window.removeEventListener(REOPEN_EVENT, onReopen);
  }, []);

  const accept = useCallback(() => {
    grantAnalyticsConsent();
    setVisible(false);
  }, []);

  const decline = useCallback(() => {
    denyAnalyticsConsent();
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] bg-[#0D2F4C] text-white border-t border-[#C9A961]/40 shadow-[0_-8px_24px_rgba(0,0,0,0.18)]"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
        <div className="max-w-3xl">
          <div
            className="text-xs uppercase tracking-[0.25em] text-[#C9A961] mb-2"
            data-testid="cookie-banner-title"
          >
            {t.cookies.title}
          </div>
          <p className="text-sm text-white/85 leading-relaxed">
            {t.cookies.message}{" "}
            <Link
              to="/politique-confidentialite"
              data-testid="cookie-banner-learn-more"
              className="text-[#C9A961] underline underline-offset-2 hover:text-[#D4B775]"
            >
              {t.cookies.learn_more}
            </Link>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full lg:w-auto">
          <button
            data-testid="cookie-decline-btn"
            onClick={decline}
            className="px-5 py-2.5 text-sm border border-white/30 text-white hover:bg-white/10 transition-colors"
            style={{ borderRadius: 0 }}
          >
            {t.cookies.decline}
          </button>
          <button
            data-testid="cookie-accept-btn"
            onClick={accept}
            className="px-5 py-2.5 text-sm bg-[#C9A961] text-[#0D2F4C] hover:bg-[#D4B775] transition-colors font-medium"
            style={{ borderRadius: 0 }}
          >
            {t.cookies.accept}
          </button>
        </div>
      </div>
    </div>
  );
};
